import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch } from '@mui/material';
import { useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updateDepot } from '../../action/depotsCommands';
import { Depot } from '../../api/types';
import { RootState } from '../../store/store';
import { DepotStatusType, updateDepotStatus } from '../../store/slices/depotSlice';
import { locationsApiSlice } from '../../api/locations/locationsApiSlice';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';

interface EditDepotDialogProps {
    open: boolean;
    onClose: () => void;
    depot: Depot;
    isEditLocker?: boolean;
}

const EditDepotDialog = (props: EditDepotDialogProps) => {
    const isEditLocker = props.isEditLocker;
    const [updateDepotLockerStatus, { isLoading }] = locationsApiSlice.useUpdateDepotStatusMutation();
    const { selectedDepotDetails } = useSelector((state: RootState) => state.depotSlice);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [enabled, setEnabled] = useState<boolean>(selectedDepotDetails?.enabled!);

    const handleClickDepotStatus = (depotStatusType: DepotStatusType) => {
        if (depotStatusType === 'enable') {
            // Update just enabled status
            dispatch(updateDepotStatus({ enabled: !selectedDepotDetails?.enabled }));
        } else {
            // Update just bookable status
            dispatch(updateDepotStatus({ bookable: !selectedDepotDetails?.status.bookable }));
        }
    };

    const handleSaveClick = async () => {
        if (isEditLocker) {
            try {
                await updateDepotLockerStatus({
                    data: {
                        status: {
                            enabled: selectedDepotDetails?.enabled!,
                            bookable: selectedDepotDetails?.status.bookable!,
                        },
                    },
                    locationId: selectedDepotDetails?.id!,
                }).unwrap();

                dispatch(
                    enqueueSuccessSnackbar({
                        message: getI18n().t('snackbar.success.update_location'),
                    }),
                );
                props.onClose();
            } catch (error) {
                dispatch(
                    enqueueSuccessSnackbar({
                        message: getI18n().t('snackbar.error.update_failed'),
                    }),
                );
            }
        } else {
            dispatch(
                updateDepot({
                    id: props.depot.id,
                    enabled,
                }),
            );

            props.onClose();
        }
    };
    return (
        <Dialog open={props.open} onClose={props.onClose} fullWidth={true}>
            <DialogTitle>{props.depot.name}</DialogTitle>
            <DialogContent>
                {isEditLocker ? (
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={selectedDepotDetails?.enabled}
                                    onChange={() => {
                                        handleClickDepotStatus('enable');
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(var(--color-primary))' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgb(var(--color-primary))' },
                                    }}
                                />
                            }
                            label={t('common.active')}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={selectedDepotDetails?.status.bookable!}
                                    onChange={() => {
                                        handleClickDepotStatus('bookable');
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(var(--color-primary))' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgb(var(--color-primary))' },
                                    }}
                                />
                            }
                            label={t('common.bookable')}
                        />
                    </Box>
                ) : (
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    onChange={() => {
                                        setEnabled(!enabled);
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(var(--color-primary))' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgb(var(--color-primary))' },
                                    }}
                                />
                            }
                            label={t('common.active')}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>{t('common.cancel')}</Button>
                <Button
                    variant={'contained'}
                    onClick={handleSaveClick}
                    sx={{
                        backgroundColor: 'rgb(var(--color-primary))',
                        '&:hover': { backgroundColor: 'rgb(var(--color-primary))', filter: 'brightness(0.9)' },
                    }}
                >
                    {isLoading ? t('common.loading') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditDepotDialog;
